<template>
  <div>
    <header
      ref="header"
      class="layout__col-2-1 header"
      :class="[
        { 'header--overflow': headerOverflow },
        { 'header--table-chrome': chrome },
        { 'header--table-documents': isDocumentsView },
        getViewHeaderClassNames(view),
      ]"
    >
      <div v-show="tableLoading" class="header__loading"></div>

      <!-- Linha 1 (chrome): breadcrumb + estrela + ações da lista -->
      <div v-if="chrome && !tableLoading" class="table-chrome__crumbs">
        <nuxt-link class="table-chrome__crumb" :to="workspaceHomeRoute">
          <span class="table-chrome__crumb-name">{{ database.name }}</span>
        </nuxt-link>
        <span class="table-chrome__crumb-separator">/</span>
        <a class="table-chrome__crumb table-chrome__crumb--current">
          <span class="table-chrome__crumb-name">{{ table.name }}</span>
          <i
            v-tooltip="$t('tableChrome.visibleToWorkspace')"
            class="table-chrome__crumb-lock iconoir-lock"
          ></i>
        </a>
        <template v-if="isDocumentsView">
          <span class="table-chrome__crumb-separator">/</span>
          <span class="table-chrome__crumb table-chrome__crumb--view">
            <i class="table-chrome__crumb-view-icon iconoir-page"></i>
            <span class="table-chrome__crumb-name">
              {{ $t('rowLinkedDocuments.title') }}
            </span>
          </span>
        </template>
        <template v-else-if="hasSelectedView">
          <span class="table-chrome__crumb-separator">/</span>
          <a
            ref="crumbViewToggle"
            class="table-chrome__crumb table-chrome__crumb--view"
            @click="views !== null && openTableViewsContext()"
          >
            <i
              class="table-chrome__crumb-view-icon"
              :class="`${view._.type.colorClass} ${view._.type.iconClass}`"
            ></i>
            <span class="table-chrome__crumb-name">{{ view.name }}</span>
            <i
              v-if="views !== null"
              class="table-chrome__crumb-caret iconoir-nav-arrow-down"
            ></i>
          </a>
          <ViewsContext
            v-if="views !== null"
            ref="viewsContext"
            :database="database"
            :table="table"
            :views="views"
            :read-only="readOnly"
            :header-overflow="headerOverflow"
            :store-prefix="storePrefix"
            @selected-view="$emit('selected-view', $event)"
          ></ViewsContext>
        </template>
        <a
          v-tooltip="
            isStarred ? $t('tableChrome.unstar') : $t('tableChrome.star')
          "
          class="table-chrome__star"
          :class="{ 'table-chrome__star--active': isStarred }"
          @click="toggleStar()"
        >
          <i class="iconoir-star"></i>
        </a>
        <div class="table-chrome__crumb-actions">
          <a
            v-if="!readOnly"
            class="table-chrome__crumb-action"
            @click="openAgents()"
          >
            <i class="iconoir-sparks"></i>
            {{ $t('tableChrome.agents') }}
          </a>
          <a
            v-if="canConfigureWebhooks"
            class="table-chrome__crumb-action"
            @click="$refs.webhookModal.show()"
          >
            <i class="iconoir-flash"></i>
            {{ $t('tableChrome.automate') }}
          </a>
          <div
            v-if="
              hasSelectedView &&
              !isDocumentsView &&
              view._.type.canShare &&
              !readOnly &&
              $hasPermission(
                'database.table.view.update_slug',
                view,
                database.workspace.id
              )
            "
            class="table-chrome__share"
          >
            <ShareViewLink
              :view="view"
              :read-only="readOnly"
              :fields="fields"
              :views="views"
              :store-prefix="storePrefix"
            ></ShareViewLink>
          </div>
        </div>
        <WebhookModal
          v-if="canConfigureWebhooks"
          ref="webhookModal"
          :database="database"
          :table="table"
          :fields="fields"
          :views="views"
        />
      </div>

      <!-- Linha 2 (chrome): abas de views -->
      <div
        v-if="chrome && !tableLoading"
        class="table-chrome__tabs"
        data-highlight="views"
      >
        <div
          v-for="tabView in orderedTabViews"
          :key="'tab-' + tabView.id"
          class="table-chrome__tab"
          :class="{
            'table-chrome__tab--active':
              !isDocumentsView && hasSelectedView && view.id === tabView.id,
          }"
          @click="
            !isDocumentsView && hasSelectedView && view.id === tabView.id
              ? undefined
              : $emit('selected-view', tabView)
          "
        >
          <i
            class="table-chrome__tab-icon"
            :class="`${tabView._.type.colorClass} ${tabView._.type.iconClass}`"
          ></i>
          <template
            v-if="!isDocumentsView && hasSelectedView && view.id === tabView.id"
          >
            <EditableViewName ref="tabRename" :view="view"></EditableViewName>
            <a
              v-if="!readOnly && showViewContext"
              ref="viewSelectToggle"
              class="table-chrome__tab-more"
              data-highlight="view-options"
              @click.stop="openTableViewContext"
            >
              <i class="baserow-icon-more-vertical"></i>
            </a>
          </template>
          <template v-else>{{ tabView.name }}</template>
        </div>
        <button
          v-if="!readOnly && !isPublic"
          type="button"
          class="table-chrome__tab table-chrome__tab--documents"
          :class="{ 'table-chrome__tab--active': isDocumentsView }"
          :aria-pressed="isDocumentsView"
          @click="$emit('selected-documents')"
        >
          <i class="table-chrome__tab-icon iconoir-page" aria-hidden="true"></i>
          {{ $t('rowLinkedDocuments.title') }}
        </button>
        <a
          v-if="
            !readOnly &&
            views !== null &&
            $hasPermission(
              'database.table.create_view',
              table,
              database.workspace.id
            )
          "
          ref="createViewToggle"
          class="table-chrome__tab-add"
          @click="
            $refs.createViewContext.toggle(
              $refs.createViewToggle,
              'bottom',
              'left',
              4
            )
          "
        >
          <i class="iconoir-plus"></i>
          {{ $t('tableChrome.addView') }}
        </a>
        <Context
          v-if="views !== null && !readOnly"
          ref="createViewContext"
          class="table-chrome__create-view-context"
          overflow-scroll
          max-height-if-outside-viewport
        >
          <div class="context__menu-title">
            {{ $t('tableChrome.addView') }}
          </div>
          <ul class="context__menu">
            <li
              v-for="(viewType, viewTypeName) in viewTypes"
              :key="viewTypeName"
              class="context__menu-item"
            >
              <CreateViewLink
                :database="database"
                :table="table"
                :view-type="viewType"
                @created="onViewCreated"
              ></CreateViewLink>
            </li>
          </ul>
        </Context>
        <ViewContext
          v-if="
            hasSelectedView && !isDocumentsView && !readOnly && showViewContext
          "
          ref="viewContext"
          :database="database"
          :view="view"
          :table="table"
          :views="views"
          :store-prefix="storePrefix"
          @enable-rename="enableRename()"
        >
        </ViewContext>
      </div>

      <div
        v-if="!tableLoading && !isDocumentsView"
        :class="chrome ? 'table-chrome__toolbar' : 'header__chrome-row'"
      >
        <ul class="header__filter">
          <li v-if="showLogo" class="header__filter-item">
            <ExternalLinkBaserowLogo class="header__filter-logo" />
          </li>
          <li
            v-if="!chrome"
            class="header__filter-item header__filter-item--grids"
          >
            <a
              ref="viewsSelectToggle"
              class="header__filter-link active"
              :class="{ 'header__filter-link--disabled': views === null }"
              data-highlight="views"
              @click="views !== null && openTableViewsContext()"
            >
              <template v-if="hasSelectedView">
                <i
                  class="header__filter-icon header-filter-icon--view"
                  :class="`${view._.type.colorClass} ${view._.type.iconClass}`"
                ></i>
                <span class="header__filter-name header__filter-name--forced">
                  <EditableViewName
                    ref="rename"
                    :view="view"
                  ></EditableViewName>
                </span>
                <i
                  v-if="views !== null"
                  class="header__sub-icon iconoir-nav-arrow-down"
                ></i>
              </template>
              <template v-else-if="view !== null">
                {{ $t('table.chooseView') }}
                <i class="header__sub-icon iconoir-nav-arrow-down"></i>
              </template>
            </a>
            <ViewsContext
              v-if="views !== null"
              ref="viewsContext"
              :database="database"
              :table="table"
              :views="views"
              :read-only="readOnly"
              :header-overflow="headerOverflow"
              :store-prefix="storePrefix"
              @selected-view="$emit('selected-view', $event)"
            ></ViewsContext>
          </li>
          <li
            v-if="!chrome && hasSelectedView && !readOnly && showViewContext"
            class="header__filter-item header__filter-item--no-margin-left"
          >
            <a
              ref="viewSelectToggle"
              class="header__filter-link"
              data-highlight="view-options"
              @click="openTableViewContext"
            >
              <i class="header__filter-icon baserow-icon-more-vertical"></i>
            </a>
            <ViewContext
              ref="viewContext"
              :database="database"
              :view="view"
              :table="table"
              :views="views"
              :store-prefix="storePrefix"
              @enable-rename="enableRename()"
            >
            </ViewContext>
          </li>
          <component
            :is="component"
            v-for="(component, index) in getAdditionalTableHeaderComponents(
              view,
              isPublic
            )"
            :key="index"
            :database="database"
            :table="table"
            :view="view"
            :fields="fields"
            :is-public-view="isPublic"
            :store-prefix="storePrefix"
          >
          </component>
          <!-- Origem Flow: o toggle de agrupar vem PRIMEIRO na toolbar,
               como o pill "Status" da lista do ClickUp. -->
          <li
            v-if="
              hasSelectedView &&
              view._.type.canGroupBy &&
              (adhocGrouping ||
                $hasPermission(
                  'database.table.view.create_group_by',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
            data-highlight="view-group-by"
          >
            <ViewGroupBy
              :database="database"
              :view="view"
              :fields="fields"
              :read-only="adhocGrouping"
              :disable-group-by="disableGroupBy"
              :store-prefix="storePrefix"
              @changed="refresh()"
            ></ViewGroupBy>
          </li>
          <li
            v-if="
              hasSelectedView &&
              view._.type.canFilter &&
              (adhocFiltering ||
                $hasPermission(
                  'database.table.view.create_filter',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
            data-highlight="view-filters"
          >
            <ViewFilter
              :view="view"
              :is-public-view="isPublic"
              :fields="fields"
              :database="database"
              :read-only="adhocFiltering"
              :disable-filter="disableFilter"
              @changed="refresh()"
            ></ViewFilter>
          </li>
          <li
            v-if="
              hasSelectedView &&
              view._.type.canSort &&
              (adhocSorting ||
                $hasPermission(
                  'database.table.view.create_sort',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
            data-highlight="view-sorts"
          >
            <ViewSort
              :database="database"
              :view="view"
              :fields="fields"
              :read-only="adhocSorting"
              :disable-sort="disableSort"
              :store-prefix="storePrefix"
              @changed="refresh()"
            ></ViewSort>
          </li>
          <li
            v-if="
              !chrome &&
              hasSelectedView &&
              view._.type.canShare &&
              !readOnly &&
              $hasPermission(
                'database.table.view.update_slug',
                view,
                database.workspace.id
              )
            "
            class="header__filter-item"
          >
            <ShareViewLink
              :view="view"
              :read-only="readOnly"
              :fields="fields"
              :views="views"
              :store-prefix="storePrefix"
            ></ShareViewLink>
          </li>
          <li
            v-if="
              hasSelectedView &&
              hasCompatibleDecorator &&
              (adhocDecorations ||
                $hasPermission(
                  'database.table.view.create_decoration',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
          >
            <ViewDecoratorMenu
              :database="database"
              :view="view"
              :table="table"
              :fields="fields"
              :read-only="adhocDecorations"
              :disable-sort="disableSort"
              :store-prefix="storePrefix"
              @changed="refresh()"
            ></ViewDecoratorMenu>
          </li>
        </ul>
        <component
          :is="getViewHeaderComponent(view)"
          v-if="hasSelectedView"
          :database="database"
          :table="table"
          :view="view"
          :fields="fields"
          :read-only="readOnly"
          :store-prefix="storePrefix"
          @refresh="refresh"
        />
        <div v-if="chrome" class="table-chrome__toolbar-right">
          <button
            v-if="canCreateRow"
            class="table-chrome__add-row"
            @click="createRowFromChrome()"
          >
            <i class="iconoir-plus"></i>
            {{ $t('tableChrome.addRow') }}
          </button>
        </div>
      </div>
    </header>
    <div
      class="layout__col-2-2 content"
      :class="{
        'content--table-chrome': chrome,
        'content--table-documents': isDocumentsView,
      }"
    >
      <TableLinkedDocuments
        v-if="isDocumentsView && !tableLoading"
        :key="`${database.workspace.id}-${table.id}`"
        :database="database"
        :table="table"
      />
      <DefaultErrorPage
        v-if="!isDocumentsView && viewError"
        :error="viewError"
      />
      <component
        :is="getViewComponent(view)"
        v-if="
          !isDocumentsView && hasSelectedView && !tableLoading && !viewError
        "
        ref="view"
        :database="database"
        :table="table"
        :view="view"
        :loading="viewLoading"
        :fields="fields"
        :read-only="readOnly"
        :store-prefix="storePrefix"
        @refresh="refresh"
        @selected-row="$emit('selected-row', $event)"
        @navigate-previous="
          (row, activeSearchTerm) =>
            $emit('navigate-previous', row, activeSearchTerm)
        "
        @navigate-next="
          (row, activeSearchTerm) =>
            $emit('navigate-next', row, activeSearchTerm)
        "
      />
      <div v-if="viewLoading && !isDocumentsView" class="loading-overlay"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { RefreshCancelledError } from '@baserow/modules/core/errors'
import { notifyIf } from '@baserow/modules/core/utils/error'
import { consumePendingAddRow } from '@baserow/modules/core/search/commandActions'
import {
  getStatusField,
  isDueDateField,
} from '@baserow/modules/database/utils/dueDate'
import { getAssigneeField } from '@baserow/modules/database/utils/subtasks'
import ViewsContext from '@baserow/modules/database/components/view/ViewsContext'
import ViewContext from '@baserow/modules/database/components/view/ViewContext'
import ViewFilter from '@baserow/modules/database/components/view/ViewFilter'
import ViewSort from '@baserow/modules/database/components/view/ViewSort'
import ViewDecoratorMenu from '@baserow/modules/database/components/view/ViewDecoratorMenu'
import ViewSearch from '@baserow/modules/database/components/view/ViewSearch'
import EditableViewName from '@baserow/modules/database/components/view/EditableViewName'
import ShareViewLink from '@baserow/modules/database/components/view/ShareViewLink'
import ExternalLinkBaserowLogo from '@baserow/modules/core/components/ExternalLinkBaserowLogo'
import ViewGroupBy from '@baserow/modules/database/components/view/ViewGroupBy'
import DefaultErrorPage from '@baserow/modules/core/components/DefaultErrorPage'
import WebhookModal from '@baserow/modules/database/components/webhook/WebhookModal'
import CreateViewLink from '@baserow/modules/database/components/view/CreateViewLink'
import TableLinkedDocuments from '@baserow/modules/database/components/table/TableLinkedDocuments'
import { waitFor } from '@baserow/modules/core/utils/queue'

/**
 * This page component is the skeleton for a table. Depending on the selected view it
 * will load the correct components into the header and body.
 */
export default {
  name: 'Table',
  components: {
    DefaultErrorPage,
    ViewGroupBy,
    ExternalLinkBaserowLogo,
    ShareViewLink,
    EditableViewName,
    ViewsContext,
    ViewDecoratorMenu,
    ViewFilter,
    ViewSort,
    ViewSearch,
    ViewContext,
    WebhookModal,
    CreateViewLink,
    TableLinkedDocuments,
  },
  /**
   * Because there is no hook that is called before the route changes, we need the
   * tableLoading middleware to change the table loading state. This change will get
   * rendered right away. This allows us to have a custom loading animation when
   * switching views.
   */
  middleware: ['tableLoading'],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    views: {
      required: false,
      validator: (prop) => typeof prop === 'object' || prop === undefined,
      default: null,
    },
    view: {
      required: true,
      validator: (prop) => typeof prop === 'object' || prop === undefined,
    },
    viewError: {
      required: false,
      validator: (prop) =>
        typeof prop === 'object' ||
        typeof prop === 'function' ||
        prop === undefined,
      default: null,
    },
    tableLoading: {
      type: Boolean,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableSort: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableGroupBy: {
      type: Boolean,
      required: false,
      default: false,
    },
    storePrefix: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Ativa o chrome estilo ClickUp (breadcrumb + abas de views + toolbar em
     * 3 linhas). Usado apenas na página principal da tabela; previews,
     * templates e views públicas mantêm o header compacto original.
     */
    chrome: {
      type: Boolean,
      required: false,
      default: false,
    },
    documentsSelected: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'navigate-next',
    'navigate-previous',
    'selected-row',
    'selected-view',
    'selected-documents',
  ],
  data() {
    return {
      // Shows a small spinning loading animation when the view is being refreshed.
      viewLoading: false,
      // Indicates if the elements within the header are overflowing. In case of true,
      // we can hide certain values to make sure that it fits within the header.
      headerOverflow: false,
      // Indicates whether the table is currently being refreshed using via the
      // `refresh-table` signal.
      isRefreshing: false,
    }
  },
  computed: {
    isDocumentsView() {
      return (
        this.chrome &&
        !this.readOnly &&
        !this.isPublic &&
        this.documentsSelected
      )
    },
    /**
     * Indicates if there is a selected view by checking if the view object has been
     * populated.
     */
    hasSelectedView() {
      return (
        this.view !== undefined &&
        Object.prototype.hasOwnProperty.call(this.view, '_')
      )
    },
    hasCompatibleDecorator() {
      return (
        this.view !== undefined &&
        this.$registry
          .getOrderedList('viewDecorator')
          .some((deco) => deco.isCompatible(this.view))
      )
    },
    showLogo() {
      return this.view?.show_logo && this.isPublic
    },
    showViewContext() {
      return (
        this.$hasPermission(
          'database.table.run_export',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.import_rows',
          this.table,
          this.database.workspace.id
        ) ||
        this.someViewHasPermission(
          'database.table.view.duplicate',
          this.table,
          this.database.workspace.id
        ) ||
        this.someViewHasPermission(
          'database.table.view.update',
          this.table,
          this.database.workspace.id
        ) ||
        this.someViewHasPermission(
          'database.table.view.delete',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.create_webhook',
          this.table,
          this.database.workspace.id
        )
      )
    },
    adhocFiltering() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_filter',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_filter',
          this.view,
          this.database.workspace.id
        )
      )
    },
    adhocSorting() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_sort',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_sort',
          this.view,
          this.database.workspace.id
        )
      )
    },
    adhocGrouping() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_group_bys',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_group_by',
          this.view,
          this.database.workspace.id
        )
      )
    },
    adhocDecorations() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_decoration',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_decoration',
          this.view,
          this.database.workspace.id
        )
      )
    },
    /**
     * O endpoint de views devolve o array agrupado por tipo; as abas seguem
     * o campo `order` global (como o ClickUp), não o agrupamento.
     */
    orderedTabViews() {
      return [...(this.views || [])].sort(
        (a, b) => a.order - b.order || a.id - b.id
      )
    },
    workspaceHomeRoute() {
      return {
        name: 'workspace',
        params: { workspaceId: this.database.workspace.id },
      }
    },
    isStarred() {
      return this.$store.getters['favorites/isTableStarred'](this.table.id)
    },
    canConfigureWebhooks() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.create_webhook',
          this.table,
          this.database.workspace.id
        )
      )
    },
    viewTypes() {
      return this.$registry.getAll('view')
    },
    canCreateRow() {
      return (
        this.hasSelectedView &&
        !this.readOnly &&
        ['grid', 'kanban', 'gallery'].includes(this.view.type) &&
        this.$hasPermission(
          'database.table.create_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    ...mapGetters({
      isPublic: 'page/view/public/getIsPublic',
    }),
  },
  watch: {
    tableLoading(value) {
      if (!value) {
        this.$nextTick(() => {
          this.checkHeaderOverflow()
          this.consumePendingCommand()
        })
      }
    },
  },
  beforeMount() {
    this.$bus.$on('table-refresh', this.refresh)
    this.$bus.$on('origem-command-add-row', this.addRowFromCommand)
    this.$bus.$on('open-table-views-context', this.openTableViewsContext)
    this.$bus.$on('close-table-views-context', this.closeTableViewsContext)
  },
  mounted() {
    this.$el.resizeObserver = new ResizeObserver(this.checkHeaderOverflow)
    this.$el.resizeObserver.observe(this.$el)

    this.$store.dispatch('favorites/load').catch(() => {
      // Favoritos são acessório: falha de rede não deve travar a tabela.
    })
    this.consumePendingCommand()
  },
  beforeUnmount() {
    this.$bus.$off('table-refresh', this.refresh)
    this.$bus.$off('origem-command-add-row', this.addRowFromCommand)
    if (this.$el?.resizeObserver) {
      this.$el.resizeObserver.unobserve(this.$el)
    }
    this.$bus.$off('open-table-views-context', this.openTableViewsContext)
    this.$bus.$off('close-table-views-context', this.closeTableViewsContext)
  },
  methods: {
    getViewComponent(view) {
      const type = this.$registry.get('view', view.type)
      return type.getComponent()
    },
    getViewHeaderComponent(view) {
      const type = this.$registry.get('view', view.type)
      return type.getHeaderComponent()
    },
    getViewHeaderClassNames(view) {
      if (!this.hasSelectedView || this.isDocumentsView) {
        return ''
      }
      const type = this.$registry.get('view', view.type)
      return type.getHeaderClassNames(view)
    },
    getAdditionalTableHeaderComponents(view, isPublic) {
      const opts = Object.values(this.$registry.getAll('plugin'))
        .reduce((components, plugin) => {
          components = components.concat(
            plugin.getAdditionalTableHeaderComponents(view, isPublic)
          )
          return components
        }, [])
        .filter((component) => component !== null)
      return opts
    },
    /**
     * When the window resizes, we want to check if the content of the header is
     * overflowing. If that is the case, we want to make some space by removing some
     * content. We do this by copying the header content into a new HTMLElement and
     * check if the elements still fit within the header. We copy the html because we
     * want to measure the header in the full width state.
     */
    checkHeaderOverflow() {
      const header = this.$refs.header
      const width = header.getBoundingClientRect().width
      const wrapper = document.createElement('div')
      wrapper.innerHTML = header.outerHTML
      const el = wrapper.childNodes[0]
      el.style = `position: absolute; left: 0; top: 0; width: ${width}px; overflow: auto;`
      el.classList.remove('header--overflow')
      document.body.appendChild(el)
      this.headerOverflow =
        el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight
      document.body.removeChild(el)
    },
    /**
     * Refreshes the whole view. All data will be reloaded and it will visually look
     * the same as seeing the view for the first time.
     */
    async refresh(event) {
      // If could be that the refresh event is for a specific table and in table case
      // we check if the refresh event is related to this table and stop if that is not
      // the case.
      if (
        typeof event === 'object' &&
        Object.prototype.hasOwnProperty.call(event, 'tableId') &&
        event.tableId !== this.table.id
      ) {
        return
      }

      const callCallback = async () => {
        if (event && Object.prototype.hasOwnProperty.call(event, 'callback')) {
          await event.callback()
        }
      }

      if (this.isRefreshing) {
        try {
          // If the table is already refreshing, then we don't have to do anything,
          // apart from waiting for the refresh to complete.
          await waitFor(() => !this.isRefreshing, 5, 30000)
        } catch (error) {
          // If the timeout is reached, then the table has been refreshing for too
          // long
        }
        // Regardless of the refreshing was completed, the callback must be called,
        // otherwise, the state of the table might not be updated correctly.
        await callCallback()
        return
      }

      this.isRefreshing = true

      const includeFieldOptions =
        typeof event === 'object' ? event.includeFieldOptions : false

      const fieldsToRefresh =
        typeof event === 'object' && event.newField
          ? [...this.fields, event.newField]
          : this.fields

      this.viewLoading = true
      const type = this.$registry.get('view', this.view.type)
      try {
        await type.refresh(
          { store: this.$store, app: this },
          this.database,
          this.view,
          fieldsToRefresh,
          this.storePrefix,
          includeFieldOptions,
          event?.sourceEvent
        )
      } catch (error) {
        if (error instanceof RefreshCancelledError) {
          // Multiple refresh calls have been made and the view has indicated that
          // this particular one should be cancelled. However we do not want to
          // set viewLoading back to false as the other non cancelled call/s might
          // still be loading.
          return
        } else {
          notifyIf(error)
        }
      }
      if (typeof this.$refs.view?.refresh === 'function') {
        await this.$refs.view.refresh()
      }
      // Before the callback is called, mark the table as not refreshing anymore, so
      // that other callbacks that are waiting can be resolved the moment the table has
      // been refreshed.
      this.isRefreshing = false
      // It might be possible that the event has a callback that needs to be called
      // after the rows are refreshed. This is for example the case when a field has
      // changed. In that case we want to update the field in the store after the rows
      // have been refreshed to prevent incompatible values in field types.
      await callCallback()
      this.$nextTick(() => {
        this.viewLoading = false
      })
    },
    someViewHasPermission(op) {
      return this.views.some((v) =>
        this.$hasPermission(op, v, this.database.workspace.id)
      )
    },
    openTableViewsContext() {
      // No modo chrome o dropdown de views abre pelo chip da view no
      // breadcrumb; no modo clássico, pelo seletor do header.
      const target = this.$refs.viewsSelectToggle || this.$refs.crumbViewToggle
      if (!this.$refs.viewsContext || !target) {
        return
      }
      this.$refs.viewsContext.toggle(target, 'bottom', 'left', 4)
    },
    closeTableViewsContext() {
      this.$refs.viewsContext?.hide()
    },
    openTableViewContext() {
      // No modo chrome o toggle vive dentro do v-for das abas, então o ref
      // chega como array.
      const toggle = Array.isArray(this.$refs.viewSelectToggle)
        ? this.$refs.viewSelectToggle[0]
        : this.$refs.viewSelectToggle
      if (!toggle) {
        return
      }
      this.$refs.viewContext.toggle(toggle, 'bottom', 'left', 4)
    },
    enableRename() {
      const ref = this.$refs.tabRename || this.$refs.rename
      const instance = Array.isArray(ref) ? ref[0] : ref
      instance?.edit()
    },
    toggleStar() {
      this.$store
        .dispatch('favorites/toggleTable', this.table.id)
        .catch(notifyIf)
    },
    /**
     * "Agentes": abre a primeira automação do workspace ou explica que ainda
     * não existe nenhuma.
     */
    openAgents() {
      const workspace = this.database.workspace
      const automations = this.$store.getters['application/getAllOfWorkspace'](
        workspace
      ).filter((application) => application.type === 'automation')
      for (const automation of automations) {
        let workflows = []
        try {
          workflows =
            this.$store.getters['automationWorkflow/getWorkflows'](automation)
        } catch (error) {
          workflows = automation.workflows || []
        }
        if (workflows.length > 0) {
          this.$router.push({
            name: 'automation-workflow',
            params: {
              automationId: automation.id,
              workflowId: workflows[0].id,
            },
          })
          return
        }
      }
      this.$store.dispatch('toast/info', {
        title: this.$t('tableChrome.agents'),
        message: this.$t('tableChrome.noAutomations'),
      })
    },
    onViewCreated(view) {
      this.$refs.createViewContext?.hide()
      this.$emit('selected-view', view)
    },
    /**
     * Botão verde "+ Tarefa". Na Lista agrupada abre o input de título do
     * primeiro grupo (nada é salvo sem título — mesma regra do
     * "+ Adicionar tarefa"); nas demais views cria a linha direto.
     */
    /**
     * Origem Flow: "Nova tarefa em X" vindo do ⌘K. Com título cria a linha
     * já nomeada; sem título segue o mesmo caminho do botão "+ Tarefa".
     * Quando a ação partiu de outra página, a criação fica pendente até a
     * tabela terminar de carregar (consumePendingCommand).
     */
    consumePendingCommand() {
      if (!this.table || !this.table.id || this.tableLoading) {
        return
      }
      const pending = consumePendingAddRow(this.table.id)
      if (pending) {
        this.addRowFromCommand(pending)
      }
    },
    async addRowFromCommand({ tableId, title = '', intent = null }) {
      if (!this.table || tableId !== this.table.id) {
        return
      }
      const trimmed = (title || '').trim()
      if (!trimmed) {
        this.createRowFromChrome()
        return
      }
      const primary = (this.fields || []).find((field) => field.primary)
      const values = primary ? { [`field_${primary.id}`]: trimmed } : {}
      Object.assign(values, this.intentToValues(intent))
      try {
        await this.$store.dispatch(
          `${this.storePrefix}view/${this.view.type}/createNewRow`,
          {
            view: this.view,
            table: this.table,
            fields: this.fields,
            values,
          }
        )
        this.$store.dispatch('toast/info', {
          title: this.$t('tableChrome.taskCreatedTitle'),
          message: this.$t('tableChrome.taskCreated', { title: trimmed }),
        })
      } catch (error) {
        notifyIf(error)
      }
    },
    /**
     * Traduz o que a captura em linguagem natural entendeu (data, hora,
     * prioridade, responsável) nos campos desta tabela. Campos ausentes são
     * ignorados em silêncio — o título sempre entra.
     */
    intentToValues(intent) {
      if (!intent) {
        return {}
      }
      const values = {}
      const fields = this.fields || []
      if (intent.date) {
        const dueField =
          fields.find(
            (field) => field.type === 'date' && isDueDateField(field)
          ) || null
        if (dueField) {
          if (dueField.date_include_time) {
            const local = new Date(
              `${intent.date}T${intent.time || '09:00'}:00`
            )
            values[`field_${dueField.id}`] = local.toISOString()
          } else {
            values[`field_${dueField.id}`] = intent.date
          }
        }
      }
      if (intent.priority) {
        const normalize = (text) =>
          (text || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        const priorityField = fields.find(
          (field) =>
            field.type === 'single_select' &&
            ['prioridade', 'priority'].includes(normalize(field.name)) &&
            field !== getStatusField(fields)
        )
        const option = priorityField
          ? (priorityField.select_options || []).find((item) =>
              normalize(item.value).startsWith(intent.priority)
            )
          : null
        if (priorityField && option) {
          values[`field_${priorityField.id}`] = option
        }
      }
      if (intent.assigneeId) {
        const assigneeField = getAssigneeField(fields)
        if (assigneeField) {
          values[`field_${assigneeField.id}`] = [
            { id: intent.assigneeId, name: intent.assigneeName || '' },
          ]
        }
      }
      return values
    },
    async createRowFromChrome() {
      if (this.view.type === 'grid' && (this.view.group_bys || []).length) {
        this.$bus.$emit('origem-open-first-ghost')
        return
      }
      try {
        await this.$store.dispatch(
          `${this.storePrefix}view/${this.view.type}/createNewRow`,
          {
            view: this.view,
            table: this.table,
            fields: this.fields,
            values: {},
          }
        )
      } catch (error) {
        notifyIf(error)
      }
    },
  },
}
</script>
